/**
 * Accessibility (ADA) enhancements
 * -----------------------------------------------------------------------------
 * Dedicated home for ADA fixes — never add these to theme.css / app.js /
 * utilities.js / custom.js (see CLAUDE.md).
 *
 * Navigation — items that own a sub menu:
 *
 *   1. The parent item is announced distinctly, e.g.
 *        collapsed -> "Womens, dropdown menu, press Enter to open"
 *        expanded  -> "Womens, dropdown menu, expanded, press Escape to close"
 *      The hint is rewritten whenever the menu opens or closes, so returning
 *      focus to the parent item never repeats a stale "press Enter to open".
 *
 *   2. aria-expanded / aria-controls are moved onto the focusable element. The
 *      theme ships them on the non-focusable wrapper <div>, where assistive
 *      tech (and the UserWay reader) never reaches them.
 *
 *   3. Escape closes an open menu and returns focus to its parent item.
 *
 *   4. Sub menu toggles that no keyboard could reach — second level items in
 *      the desktop dropdown, and the caret in the mobile menu — are exposed as
 *      real buttons, so their child links stop being a keyboard dead end.
 *
 * Covers the classic, centered, vertical and search-focus headers, the sticky
 * header clone, the mobile menu, and mega menus (which are injected after load).
 *
 * To localise, define window.PXUTheme.a11yMenuStrings = { collapsed: '…',
 * expanded: '…', expandedToggle: '…' } before this file runs.
 */
(function () {
  'use strict';

  var DEFAULT_STRINGS = {
    // Shown on a collapsed parent item.
    collapsed: 'dropdown menu, press Enter to open',
    // Shown on an expanded parent item that is also a link — a second Enter
    // follows the link, so Escape is what closes it.
    expanded: 'dropdown menu, expanded, press Escape to close',
    // Shown on an expanded parent item that is only a toggle.
    expandedToggle: 'dropdown menu, expanded, press Enter to close'
  };

  var strings = {};
  var overrides =
    (window.PXUTheme && window.PXUTheme.a11yMenuStrings) || {};

  Object.keys(DEFAULT_STRINGS).forEach(function (key) {
    strings[key] = overrides[key] || DEFAULT_STRINGS[key];
  });

  var TOP_LEVEL_SELECTOR = [
    '.navbar-item.has-dropdown',
    '.navbar-item.has-mega-menu',
    '.vertical-header__first-level.has-dropdown',
    '.vertical-header__first-level.has-mega-menu'
  ].join(',');

  var SECOND_LEVEL_SELECTOR = '.navbar-dropdown .has-submenu';

  var MOBILE_SELECTOR = [
    '.mobile-menu__first-level[data-mobile-menu-has-toggle="true"]',
    '.mobile-menu__second-level'
  ].join(',');

  var DECORATED = 'data-a11y-menu-ready';
  var HINT_CLASS = 'a11y-menu-hint';

  var entries = [];
  var idCounter = 0;
  var syncQueued = false;
  var scanQueued = false;

  function uniqueId(prefix) {
    idCounter += 1;
    return prefix + idCounter;
  }

  function checkboxIn(wrapper) {
    return wrapper.querySelector(':scope > input[type="checkbox"]');
  }

  /* -------------------------------------------------------------------------
   * Desktop — top level items
   * ---------------------------------------------------------------------- */

  /* The theme wraps every navigation link in a <label>, so the trigger is
     either a direct child or one level down. */
  function linkIn(wrapper) {
    var child = wrapper.firstElementChild;

    while (child) {
      if (child.tagName === 'A') return child;
      if (child.tagName === 'LABEL') {
        var nested = child.querySelector('a');
        if (nested) return nested;
      }
      child = child.nextElementSibling;
    }

    return null;
  }

  function topLevelPopup(wrapper) {
    return (
      wrapper.querySelector(':scope > .mega-menu__section') ||
      wrapper.querySelector(':scope > .navbar-dropdown')
    );
  }

  function topLevelExpanded(wrapper) {
    if (wrapper.classList.contains('is-closed')) {
      return false;
    }

    if (
      wrapper.classList.contains('is-opened') ||
      wrapper.classList.contains('show-dropdown')
    ) {
      return true;
    }

    var mega = wrapper.querySelector('.mega-menu__section');
    if (mega && mega.classList.contains('is-active')) return true;

    var megaMenu = wrapper.querySelector('.mega-menu');
    if (
      megaMenu &&
      (megaMenu.classList.contains('mega-menu--show') ||
        megaMenu.classList.contains('mega-menu--force-show'))
    ) {
      return true;
    }

    if (document.activeElement && wrapper.contains(document.activeElement)) {
      return true;
    }

    return false;
  }

  function openTopLevel(entry) {
    entry.wrapper.classList.remove('is-closed');
    entry.wrapper.classList.add('show-dropdown', 'is-opened');

    var mega = entry.wrapper.querySelector('.mega-menu__section');
    if (mega) mega.classList.add('is-active');

    var megaMenu = entry.wrapper.querySelector('.mega-menu');
    if (megaMenu) megaMenu.classList.add('mega-menu--show');
  }

  function closeTopLevel(entry) {
    entry.wrapper.classList.add('is-closed');
    entry.wrapper.classList.remove('show-dropdown', 'is-opened');

    var mega = entry.wrapper.querySelector('.mega-menu__section');
    if (mega) mega.classList.remove('is-active');

    var megaMenu = entry.wrapper.querySelector('.mega-menu');
    if (megaMenu) {
      megaMenu.classList.remove('mega-menu--show', 'mega-menu--force-show');
    }

    var dropdown = entry.wrapper.querySelector('.navbar-dropdown');
    if (dropdown) dropdown.classList.remove('show-nested-dropdown');

    var nested = entry.wrapper.querySelectorAll(
      '.has-submenu > input[type="checkbox"]'
    );
    for (var i = 0; i < nested.length; i++) nested[i].checked = false;
  }

  /* -------------------------------------------------------------------------
   * Desktop — second level items (vertical dropdown style)
   * ---------------------------------------------------------------------- */

  function secondLevelPopup(wrapper) {
    return wrapper.querySelector(':scope > .navbar-submenu');
  }

  function secondLevelExpanded(wrapper) {
    var checkbox = checkboxIn(wrapper);
    if (checkbox && checkbox.checked) return true;

    var dropdown = wrapper.closest('.navbar-dropdown');
    return !!(dropdown && dropdown.classList.contains('show-nested-dropdown'));
  }

  function openSecondLevel(entry) {
    var checkbox = checkboxIn(entry.wrapper);
    if (checkbox) checkbox.checked = true;
  }

  function closeSecondLevel(entry) {
    var checkbox = checkboxIn(entry.wrapper);
    if (checkbox) checkbox.checked = false;

    var dropdown = entry.wrapper.closest('.navbar-dropdown');
    if (dropdown) dropdown.classList.remove('show-nested-dropdown');
  }

  /* -------------------------------------------------------------------------
   * Mobile menu
   *
   * The caret (.close-dropdown) is the only thing that opens a sub menu, and a
   * bare <span> is neither focusable nor announced. The link beside it keeps
   * its own destination, so the caret becomes a separate toggle button.
   * ---------------------------------------------------------------------- */

  function mobileToggleIn(wrapper) {
    return wrapper.querySelector(
      ':scope > label > .close-dropdown, :scope > label > .close-sub-dropdown'
    );
  }

  function mobilePopup(wrapper) {
    return wrapper.querySelector(
      ':scope > .mobile-submenu__list, :scope > .mobile-sub-submenu__list'
    );
  }

  function mobileExpanded(wrapper) {
    var checkbox = checkboxIn(wrapper);
    return !!(checkbox && checkbox.checked);
  }

  function toggleMobile(entry) {
    /* The caret sits inside the <label>, so a click still reaches the checkbox
       the theme's CSS keys off. */
    entry.trigger.click();
  }

  function mobileTitle(wrapper) {
    var link = wrapper.querySelector(':scope > label > a');
    return link ? link.textContent : '';
  }

  /* -------------------------------------------------------------------------
   * Shared wiring
   * ---------------------------------------------------------------------- */

  function setHint(entry, expanded) {
    var phrase = strings.collapsed;

    if (expanded) {
      phrase = entry.toggles ? strings.expandedToggle : strings.expanded;
    }

    if (entry.hint) {
      var text = ', ' + phrase;
      if (entry.hint.textContent !== text) entry.hint.textContent = text;
      return;
    }

    var label = (entry.title + ', ' + phrase).replace(/\s+/g, ' ').trim();
    if (entry.trigger.getAttribute('aria-label') !== label) {
      entry.trigger.setAttribute('aria-label', label);
    }
  }

  function syncEntry(entry) {
    if (!entry.trigger.isConnected) return;

    /* Mega menus are cloned in after load and replace the plain dropdown,
       which the theme then hides — re-point aria-controls at the real popup. */
    var popup = entry.popupFor(entry.wrapper);
    if (popup && popup !== entry.popup) {
      entry.popup = popup;
      if (!popup.id) popup.id = uniqueId('a11y-submenu-');
      entry.trigger.setAttribute('aria-controls', popup.id);
    }

    var expanded = entry.isExpanded(entry.wrapper);
    entry.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    setHint(entry, expanded);
  }

  function syncAll() {
    syncQueued = false;

    for (var i = entries.length - 1; i >= 0; i--) {
      if (entries[i].trigger.isConnected) {
        syncEntry(entries[i]);
      } else {
        entries.splice(i, 1);
      }
    }
  }

  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(syncAll);
  }

  function decorate(wrapper, options) {
    if (wrapper.hasAttribute(DECORATED)) return;

    var trigger = options.trigger(wrapper);
    if (!trigger) return;

    var popup = options.popup(wrapper);
    if (!popup) return;

    wrapper.setAttribute(DECORATED, 'true');

    /* The theme puts these on the wrapper <div>, which has no role and is not
       focusable — assistive tech never announces them there. */
    wrapper.removeAttribute('aria-haspopup');
    wrapper.removeAttribute('aria-expanded');

    if (!popup.id) popup.id = uniqueId('a11y-submenu-');
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-controls', popup.id);
    trigger.setAttribute('aria-expanded', 'false');

    /* Anything that is not a real link needs a role and a tab stop of its own
       before a keyboard can get to it. */
    if (!trigger.hasAttribute('href')) {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
    }

    var entry = {
      wrapper: wrapper,
      trigger: trigger,
      popup: popup,
      popupFor: options.popup,
      isExpanded: options.isExpanded,
      open: options.open,
      close: options.close,
      toggles: options.toggles === true,
      hint: null,
      title: ''
    };

    if (options.title) {
      /* Empty trigger — the name has to carry the item title itself. */
      entry.title = options.title(wrapper);
    } else {
      /* The trigger already reads out its own text, so append the hint to it. */
      entry.hint = document.createElement('span');
      entry.hint.className = HINT_CLASS;
      trigger.appendChild(entry.hint);
    }

    entries.push(entry);

    new MutationObserver(scheduleSync).observe(wrapper, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    var checkbox = checkboxIn(wrapper);
    if (checkbox) checkbox.addEventListener('change', scheduleSync);

    wrapper.addEventListener('focusout', function (e) {
      if (!wrapper.contains(e.relatedTarget)) {
        wrapper.classList.remove('is-closed');
      }
    });

    wrapper.addEventListener('mouseenter', function () {
      wrapper.classList.remove('is-closed');
    });

    syncEntry(entry);
  }

  function entryForTrigger(element) {
    for (var i = 0; i < entries.length; i++) {
      if (
        entries[i].trigger === element ||
        entries[i].trigger.contains(element)
      ) {
        return entries[i];
      }
    }
    return null;
  }

  /* First Enter opens the menu, a second Enter follows the link — the theme's
     own behaviour, but driven by the real state so it stays correct after the
     menu has been closed with Escape.

     Bound to document in the capture phase: the theme binds its handler to the
     link itself, and it must not see the event first. */
  function handleActivation(event) {
    var isEnter = event.key === 'Enter' || event.keyCode === 13;
    var isSpace = event.key === ' ' || event.keyCode === 32;
    if (!isEnter && !isSpace) return;

    var entry = entryForTrigger(event.target);
    if (!entry) return;
    if (isSpace && entry.trigger.hasAttribute('href')) return;

    var expanded = entry.isExpanded(entry.wrapper);
    if (expanded && !entry.toggles) return;

    event.preventDefault();

    if (expanded) {
      entry.close(entry);
    } else {
      entry.open(entry);
    }

    scheduleSync();
  }

  function handleEscape(event) {
    if (event.key !== 'Escape' && event.keyCode !== 27) return;

    var active = document.activeElement;
    if (!active) return;

    var searchOverlay = document.querySelector('[data-search-type="overlay"].is-opened');
    if (searchOverlay) {
      event.preventDefault();
      if (window.PXUTheme && window.PXUTheme.jsHeader && window.PXUTheme.jsHeader.hideSearch) {
        window.PXUTheme.jsHeader.hideSearch();
      }
      var searchTrigger = document.querySelector('[data-show-search-trigger]');
      if (searchTrigger) searchTrigger.focus();
      return;
    }

    var cartDrawer = document.querySelector('#theme-ajax-cart.is-drawer.is-visible');
    if (cartDrawer) {
      event.preventDefault();
      if (window.PXUTheme && window.PXUTheme.jsAjaxCart && window.PXUTheme.jsAjaxCart.hideDrawer) {
        window.PXUTheme.jsAjaxCart.hideDrawer();
      }
      var cartTrigger = document.querySelector('[data-ajax-cart-trigger]');
      if (cartTrigger) cartTrigger.focus();
      return;
    }

    /* Close the innermost open menu first, then its parent. */
    var open = null;
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.wrapper.contains(active)) continue;
      if (!entry.isExpanded(entry.wrapper)) continue;
      if (!open || open.wrapper.contains(entry.wrapper)) open = entry;
    }

    if (!open) return;

    event.preventDefault();
    open.close(open);
    open.trigger.focus();
    scheduleSync();
  }

  function decorateAll(selector, options) {
    var wrappers = document.querySelectorAll(selector);

    for (var i = 0; i < wrappers.length; i++) {
      if (options.requiresCheckbox && !checkboxIn(wrappers[i])) continue;
      decorate(wrappers[i], options);
    }
  }

  function scan() {
    scanQueued = false;

    decorateAll(TOP_LEVEL_SELECTOR, {
      trigger: linkIn,
      popup: topLevelPopup,
      isExpanded: topLevelExpanded,
      open: openTopLevel,
      close: closeTopLevel
    });

    /* The horizontal dropdown style also uses .has-submenu, but its lists are
       always visible and have no checkbox to toggle. */
    decorateAll(SECOND_LEVEL_SELECTOR, {
      requiresCheckbox: true,
      trigger: linkIn,
      popup: secondLevelPopup,
      isExpanded: secondLevelExpanded,
      open: openSecondLevel,
      close: closeSecondLevel
    });

    decorateAll(MOBILE_SELECTOR, {
      requiresCheckbox: true,
      trigger: mobileToggleIn,
      title: mobileTitle,
      popup: mobilePopup,
      isExpanded: mobileExpanded,
      open: toggleMobile,
      close: toggleMobile,
      toggles: true
    });

    syncAll();
    ensureSearchButtonTriggers();
    enhanceSearchAccessibility();
    enhanceAnnouncementBar();
    enhanceCartAccessibility();
  }

  // Cart drawer accessibility (WCAG 1.4.10, 2.1.1, 4.1.2)
  function enhanceCartAccessibility() {
    var cartDrawer = document.querySelector('#theme-ajax-cart.is-drawer');
    if (cartDrawer) {
      if (!cartDrawer.getAttribute('role')) {
        cartDrawer.setAttribute('role', 'dialog');
      }
      if (!cartDrawer.getAttribute('aria-label')) {
        cartDrawer.setAttribute('aria-label', 'Shopping Cart');
      }
    }

    var closeButtons = document.querySelectorAll('.ajax-cart__close-icon, [data-ajax-cart-close]');
    for (var i = 0; i < closeButtons.length; i++) {
      var btn = closeButtons[i];
      if (!btn.classList.contains('ajax-cart__overlay')) {
        if (!btn.getAttribute('role')) {
          btn.setAttribute('role', 'button');
        }
        if (!btn.hasAttribute('tabindex')) {
          btn.setAttribute('tabindex', '0');
        }
        if (!btn.getAttribute('aria-label')) {
          btn.setAttribute('aria-label', 'Close cart');
        }
      }
    }
  }

  // Ensure all search triggers are real buttons for screen readers and keyboard users
  function ensureSearchButtonTriggers() {
    var searchLinks = document.querySelectorAll('a[data-show-search-trigger]');
    for (var i = 0; i < searchLinks.length; i++) {
      var a = searchLinks[i];
      var btn = document.createElement('button');
      btn.type = 'button';
      for (var j = 0; j < a.attributes.length; j++) {
        var attr = a.attributes[j];
        if (attr.name !== 'href') {
          btn.setAttribute(attr.name, attr.value);
        }
      }
      btn.setAttribute('aria-haspopup', 'dialog');
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Search');
      }
      while (a.firstChild) {
        btn.appendChild(a.firstChild);
      }
      a.parentNode.replaceChild(btn, a);
    }
  }

  // Search inputs accessibility (WCAG 2.1.1, 4.1.2)
  function enhanceSearchAccessibility() {
    var searchInputs = document.querySelectorAll('form[action*="/search"] input[name="q"], [data-q]');
    for (var i = 0; i < searchInputs.length; i++) {
      var input = searchInputs[i];
      if (!input.id) {
        input.id = 'search-input-' + (i + 1);
      }
      var form = input.closest('form');
      if (form) {
        if (!form.getAttribute('role')) {
          form.setAttribute('role', 'search');
        }
        var label = form.querySelector('label[for="q"]');
        if (label) {
          label.setAttribute('for', input.id);
        }
      }
      if (!input.getAttribute('aria-label')) {
        var placeholder = input.getAttribute('placeholder');
        if (placeholder) {
          input.setAttribute('aria-label', placeholder);
        }
      }
    }
  }

  // Announcement bar keyboard focusability (WCAG 2.1.1)
  function enhanceAnnouncementBar() {
    var wraps = document.querySelectorAll(
      '#announcement-bar .is-hidden-mobile-only, #announcement-bar .is-hidden-desktop-only'
    );

    for (var i = 0; i < wraps.length; i++) {
      var target = wraps[i].firstElementChild;
      if (target && !target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '0');
      }
    }
  }

  // Handle Enter and Space keys on search trigger (WCAG 2.1.1)
  function handleSearchTriggerKeydown(event) {
    var trigger =
      event.target.closest && event.target.closest('[data-show-search-trigger]');
    if (!trigger) return;

    var isEnter = event.key === 'Enter' || event.keyCode === 13;
    var isSpace = event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32;

    if (!isEnter && !isSpace) return;

    if (isEnter) {
      if (trigger.tagName === 'BUTTON') {
        event.stopImmediatePropagation();
      } else {
        event.preventDefault();
        event.stopImmediatePropagation();
        openSearchModal(trigger);
      }
    } else if (isSpace) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openSearchModal(trigger);
    }
  }

  function openSearchModal(trigger) {
    if (window.PXUTheme && window.PXUTheme.jsHeader && window.PXUTheme.jsHeader.showSearch) {
      window.PXUTheme.jsHeader.showSearch();
    } else if (trigger) {
      trigger.click();
    }
  }

  function initSearchModalEvents() {
    // Prevent duplicate Fancybox modal instances and restore focus on close
    if (window.PXUTheme && window.PXUTheme.jsHeader) {
      if (window.PXUTheme.jsHeader.showSearch) {
        var origShowSearch = window.PXUTheme.jsHeader.showSearch;
        var searchOpening = false;
        window.PXUTheme.jsHeader.showSearch = function () {
          if (searchOpening || (window.jQuery && window.jQuery.fancybox && window.jQuery.fancybox.getInstance())) {
            return;
          }
          searchOpening = true;
          setTimeout(function () {
            searchOpening = false;
          }, 500);
          return origShowSearch.apply(this, arguments);
        };
      }

      if (window.PXUTheme.jsHeader.hideSearch) {
        var origHideSearch = window.PXUTheme.jsHeader.hideSearch;
        window.PXUTheme.jsHeader.hideSearch = function () {
          var overlay = document.querySelector('[data-search-type="overlay"]');
          var wasOpen = overlay && overlay.classList.contains('is-opened');
          var res = origHideSearch.apply(this, arguments);
          if (wasOpen) {
            var trigger = document.querySelector('[data-show-search-trigger]');
            if (trigger) {
              trigger.focus();
            }
          }
          return res;
        };
      }

    }

    if (window.jQuery) {
      window.jQuery(document).on('afterShow.fb', function (e, instance) {
        if (instance && instance.$refs && instance.$refs.container && instance.$refs.container.hasClass('search__lightbox')) {
          instance.$refs.container.find('[data-fancybox-close]').attr('aria-label', 'Close search');
        }
      });
      window.jQuery(document).on('afterClose.fb', function (e, instance) {
        if (instance && instance.$refs && instance.$refs.container && instance.$refs.container.hasClass('search__lightbox')) {
          var trigger = document.querySelector('[data-show-search-trigger]');
          if (trigger) {
            trigger.focus();
          }
        }
      });
    }
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.setTimeout(scan, 200);
  }

  function init() {
    scan();
    initSearchModalEvents();

    document.addEventListener('keydown', handleActivation, true);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('keydown', handleSearchTriggerKeydown, true);

    /* The theme toggles menus from several places (hover, click, its own
       keyboard handler) — re-read the state after anything that could change
       it rather than trying to intercept each one. */
    ['focusin', 'focusout', 'click', 'change', 'keyup'].forEach(function (type) {
      document.addEventListener(type, scheduleSync, true);
    });

    /* Mega menus are cloned into the header and the mobile menu after load, and
       the theme editor re-renders sections in place. */
    new MutationObserver(scheduleScan).observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('shopify:section:load', scheduleScan);
    window.addEventListener('load', scheduleScan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
